import {AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {DeviceEvent} from "../shared/models/device-event";
import {MatTableDataSource} from "@angular/material/table";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {DeviceDetailService} from "../shared/services/device-detail.service";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatButtonToggle} from "@angular/material/button-toggle";
import {DeviceOperationStateItem} from "../shared/models/device-operation-state-item.model";

interface DeviceEventAttribute {
  name: string;
  value: string;
}

@Component({
  selector: 'app-device-events',
  templateUrl: './device-events.component.html',
  styleUrls: ['./device-events.component.scss'],
  providers: [
    DeviceDetailService,
    {provide: 'tablePaddingHeight', useValue: 216},
    {provide: 'itemHeight', useValue: 52},
    {provide: 'paginations', useValue: [5, 10, 25, 50]},
  ],
})
export class DeviceEventsComponent implements OnInit, AfterViewInit {
  protected mobile: boolean = false;

  private _selected: DeviceEvent | undefined;

  public get selected(): DeviceEvent | undefined {
    return this._selected;
  }

  public set selected(value: DeviceEvent | undefined) {
    this._selected = value;
  }

  @Input() set deviceEventsData(
    value: DeviceEvent[] | undefined
  ) {
    this.deviceEvents.data = value ?? [];
  }

  //DeviceEvent
  @ViewChild('deviceEventItemPaginator', {static: true})
  private deviceEventItemPaginator!: MatPaginator;
  @ViewChild('deviceEventItemsDetailGroup', {static: true})
  deviceEventItemsDetailGroup!: MatButtonToggle;
  @ViewChild('deviceEventTable', {static: true})
  private deviceEventItemsSort = new MatSort();

  //DeviceEventAttribute
  @ViewChild('eventsAttrTable', {static: true})
  protected eventsAttrTable!: ElementRef<HTMLTableElement>;
  @ViewChild('deviceEventItemsDetailTable', {static: true})
  private deviceEventItemAttributesSort = new MatSort();
  @ViewChild('deviceEventsDetailPaginator', {static: true})
  private deviceEventsAttributesPaginator!: MatPaginator;


  protected readonly deviceEventColumns: (keyof DeviceEvent)[] = [
    'eventText',
    'counter',
    'recordStatus',
    'lastUpdatedTimestamp'
  ];

  protected readonly deviceEventAttrColumns: (keyof DeviceEventAttribute)[] = [
    'name',
    'value'
  ];

  protected deviceEventColumnsToggle: (keyof DeviceEvent)[] =
    ['eventText', 'counter', 'recordStatus', 'lastUpdatedTimestamp'];

  protected get filteredDeviceEventColumns(): (keyof DeviceEvent)[] {
    return this.deviceEventColumns.filter((col) =>
      this.deviceEventColumnsToggle.includes(col)
    );
  }

  protected readonly deviceEvents: MatTableDataSource<DeviceEvent> = new MatTableDataSource<DeviceEvent>();
  protected readonly deviceEventItemAttributes: MatTableDataSource<DeviceEventAttribute> = new MatTableDataSource<DeviceEventAttribute>();


  protected selectEventItem(element: DeviceEvent): void {
    this.selected = element;
    this.setSelectedEventAttributes();
    //this.calcAttrPagination();
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    private deviceDetailService: DeviceDetailService
  ) {
    this.deviceEvents.sortData = this.sortDeviceEventItems();
    this.deviceEventItemAttributes.sortData = this.sortDeviceEventAttrItems();
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe({
      next: (result) => {
        this.mobile = result.matches;

        this.calcPagination();

        if (this.selected) {
          this.calcAttrPagination();
        }
      },
    });
  }

  private calcPagination(): void {
    if (this.deviceEventItemPaginator) {
      this.deviceDetailService.calculatePagination(
        this.deviceEventItemPaginator
      );
      this.deviceEvents.paginator =
        this.deviceEventItemPaginator;
    }
  }

  private calcAttrPagination(): void {
    const tableHeight = this.eventsAttrTable.nativeElement.offsetHeight - 56;
    this.deviceDetailService.calculatePaginationByHeight(
      this.deviceEventsAttributesPaginator,
      tableHeight
    );
    this.deviceEventItemAttributes.paginator =
      this.deviceEventsAttributesPaginator;
  }

  private sortDeviceEventItems() {
    return (items: DeviceEvent[], sort: MatSort) => {
      if (!sort.active || sort.direction === '') {
        return items;
      }
      return items.sort(
        (a: DeviceEvent, b: DeviceEvent) => {
          const asc = sort.direction === 'asc' ? 1 : -1;
          const valA = a[sort.active as keyof DeviceEvent] ?? '';
          const valB = b[sort.active as keyof DeviceEvent] ?? '';
          if (typeof valA === 'number' && typeof valB === 'number') {
            return (valA - valB) * asc;
          }
          return String(valA).localeCompare(String(valB)) * asc;
        }
      );
    };
  }

  private sortDeviceEventAttrItems() {
    return (items: DeviceEventAttribute[], sort: MatSort) => {
      if (!sort.active || sort.direction === '') {
        return items;
      }
      return items.sort(
        (a: DeviceEventAttribute, b: DeviceEventAttribute) => {
          const asc = sort.direction === 'asc' ? 1 : -1;
          const valA = a[sort.active as keyof DeviceEventAttribute] ?? '';
          const valB = b[sort.active as keyof DeviceEventAttribute] ?? '';

          return String(valA).localeCompare(String(valB)) * asc;
        }
      );
    };
  }

  private setSelectedEventAttributes() {
    let result = [];
    for (const [key, value] of Object.entries(this.selected!!)) {
      result.push({name: key, value: value});
    }
    this.deviceEventItemAttributes.data = result.filter((item) => item.value || item.value === 0);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.calcPagination(), 1000);
  }

  ngOnInit() {
    this.deviceEvents.paginator = this.deviceEventItemPaginator;
    this.deviceEvents.sort = this.deviceEventItemsSort;

    this.deviceEventItemAttributes.paginator = this.deviceEventsAttributesPaginator
    this.deviceEventItemAttributes.sort = this.deviceEventItemAttributesSort;
  }

}
